import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, COMMAND_PRIORITY_LOW, KEY_DOWN_COMMAND } from 'lexical';
import { useEffect } from 'react';
import { FieldRefTarget } from '../../util/formula-vocabulary';
import { findEnclosingCalls, SLOT_OPENER, slotKind } from './enclosingCalls';
import { fieldsForContext, matchFieldTrigger } from './FieldTypeaheadPlugin';
import { matchFunctionTrigger } from './FunctionTypeaheadPlugin';
import { $displayOffsetOf, $serializeFormula } from './serialization';
import { isInsideStringLiteral } from './StringLiteralPlugin';

/**
 * What a typed "," should insert when the slot it opens only accepts one kind
 * of thing: a field-only slot gets ", [" (the field typeahead opens on the
 * trailing "["), REDUCE's last argument gets ", LAMBDA(". Picking a chip
 * usually advances on its own (see advanceAfterChip); this covers the slots
 * it deliberately leaves alone, where the argument before the comma was
 * typed. Null means the comma is plain content: any free slot, inside a
 * string literal, an unclosed "[" or right after an identifier, when text
 * (other than whitespace and ")") follows the caret — completing there would
 * corrupt an edit — or when the slot has no field to pick.
 */
export function commaCompletion(display: string, caret: number, fields: FieldRefTarget[]): string | null {
  const before = display.slice(0, caret);
  // A caret owned by a string literal or either typeahead gets a plain comma:
  // in an unclosed "[" or right after an identifier ("REDUCE(0, [Tr", "…, LA")
  // the comma edits that token, and completing would compound a stray one.
  if (isInsideStringLiteral(before) || matchFieldTrigger(before) || matchFunctionTrigger(before)) {
    return null;
  }
  if (!/^[\s)]*$/.test(display.slice(caret))) {
    return null;
  }
  // As if the comma were already typed: the in-progress argument lands in
  // args (member-slot list resolution needs it) and argIndex advances.
  const calls = findEnclosingCalls(`${before},`, caret + 1);
  const kind = slotKind(calls[0]);
  if (kind === 'free' || (kind === 'field' && !fieldsForContext(fields, calls).length)) {
    return null;
  }
  return `, ${SLOT_OPENER[kind]}`;
}

/** Comma completion for forced slots — see commaCompletion. */
export function CommaCompletionPlugin({ fields }: { fields: FieldRefTarget[] }) {
  const [editor] = useLexicalComposerContext();

  useEffect(
    () =>
      editor.registerCommand<KeyboardEvent>(
        KEY_DOWN_COMMAND,
        (event) => {
          if (event.key !== ',' || event.ctrlKey || event.metaKey || event.altKey || event.isComposing) {
            return false;
          }
          const completion = editor.getEditorState().read(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
              return null;
            }
            const caret = $displayOffsetOf(selection.anchor.getNode(), selection.anchor.offset);
            return commaCompletion($serializeFormula().display, caret, fields);
          });
          if (completion === null) {
            return false;
          }
          event.preventDefault();
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              selection.insertText(completion);
            }
          });
          return true;
        },
        COMMAND_PRIORITY_LOW
      ),
    [editor, fields]
  );

  return null;
}
